import { useAudioLevelsSubscription } from '@/app-shell/desktop/hooks/useAudioLevelsSubscription';
import { setWebAudioLevelAlertOpen } from '@/app-shell/session-page/web-audio-level-alert-state';
import {
  chronicleAmplitudeSourceAtom,
  chronicleAudioRmsAtom,
  chronicleConnectionStatusAtom,
  chronicleDeviceRecordingInfoAtom,
  chronicleIsPausedAtom,
} from '@/atoms/chronicle';
import { tauriAudioLevelsAtom } from '@/atoms/tauriAudioLevelsAtom';
import MobileModalNavBar from '@/components/ui/MobileModalNavBar';
import { useCanChangeMicrophoneForCurrentSession } from '@/hooks/useCanChangeMicrophoneForCurrentSession';
import { useTutorial } from '@/hooks/useTutorial';
import { configureCapture } from '@heidi/capture/configure';
import { useTranslations } from '@heidi/runtime/i18n';
import { SessionPages } from '@heidi/schema/ml-scribe';
import { MicrophoneSelectorV1 } from '@heidi/session';
import { useCurrentSessionIsTabSharing } from '@heidi/session/data';
import { useAtomValue } from 'jotai';
import dynamic from 'next/dynamic';

const MicTestModal = dynamic(
  () =>
    import('@/composition/hosts/capture/mic-test/mic-test-host').then((mod) => ({
      default: mod.MicTestModal,
    })),
  { ssr: false }
);

const SpeakerTestModal = dynamic(
  () =>
    import('@heidi/capture/mic-test').then((mod) => ({
      default: mod.SpeakerTestModal,
    })),
  { ssr: false }
);

const micSelector = {
  useTutorial: () => ({
    ...useTutorial(),
    checkMicrophonePage: SessionPages.CHECK_MICROPHONE,
  }),
  useCanChangeMicrophone: useCanChangeMicrophoneForCurrentSession,
  useIsTabSharingSession: useCurrentSessionIsTabSharing,
  useCopy: () => {
    const { t } = useTranslations();
    return {
      noInputDevices: t('recording.noInputDevices'),
      audioLevelAlertSilentHeading: t('recording.audioLevelAlertSilentHeading'),
      audioLevelAlertSilentBody: t('recording.audioLevelAlertSilentBody'),
      audioLevelAlertLowHeading: t('recording.audioLevelAlertLowHeading'),
      audioLevelAlertLowBody: t('recording.audioLevelAlertLowBody'),
      close: t('common.close'),
      telehealthHeading: t('telehealthMicDropdown.heading'),
      telehealthYourAudio: t('telehealthMicDropdown.yourAudio'),
      telehealthPleaseSelectAudio: t('telehealthMicDropdown.pleaseSelectAudio'),
      telehealthPatientAudio: t('telehealthMicDropdown.patientAudio'),
      telehealthPatientAudioPlaceholder: t(
        'telehealthMicDropdown.patientAudioPlaceholder'
      ),
      telehealthMicPlaceholder: t('sessionHeader.telehealthMicPlaceholder'),
    };
  },
  useNativeAudioLevels: () => {
    useAudioLevelsSubscription();
    return useAtomValue(tauriAudioLevelsAtom);
  },
  useChronicleMeter: () => {
    const { rms } = useAtomValue(chronicleAudioRmsAtom);
    const isPaused = useAtomValue(chronicleIsPausedAtom);
    const source = useAtomValue(chronicleAmplitudeSourceAtom);
    const recordingInfo = useAtomValue(chronicleDeviceRecordingInfoAtom);
    const connectionStatus = useAtomValue(chronicleConnectionStatusAtom);
    return {
      rms,
      isPaused,
      source,
      recordingSessionId: recordingInfo?.sessionId,
      connectionStatus,
    };
  },
  LegacyMicrophoneSelector: MicrophoneSelectorV1,
  MicTestModal,
  SpeakerTestModal,
  MobileNavBar: MobileModalNavBar,
  reportWebAudioLevelAlert: setWebAudioLevelAlertOpen,
};

configureCapture({ hostInputs: { micSelector } });
